import React, { useEffect, useMemo, useState } from "react";
import { FaChartBar, FaChevronLeft, FaChevronRight, FaMoneyCheckAlt } from "react-icons/fa";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import {
  formatStatMoney,
  tradeBelongsToAgent,
} from "../utils/dashboardStatsUtils";
import {
  currentMonthCursor,
  shiftMonth,
  monthTitle,
  buildCommissionReceivedForMonth,
  buildEftsPaidOutForMonth,
  formatChartAxis,
} from "../utils/dashboardMonthlyChartUtils";

function DailyBarChart({ data, color }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  const width = 520;
  const height = 200;
  const padL = 44;
  const padR = 8;
  const padT = 12;
  const padB = 28;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const slot = innerW / Math.max(data.length, 1);
  const barW = Math.max(3, slot * 0.7);
  const ticks = [0, 0.5, 1];
  const lastDay = data.length;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-52" role="img">
      {ticks.map((t) => {
        const y = padT + innerH * (1 - t);
        return (
          <g key={t}>
            <line
              x1={padL}
              x2={width - padR}
              y1={y}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
            <text
              x={padL - 4}
              y={y + 3}
              textAnchor="end"
              className="fill-gray-400"
              fontSize="10"
            >
              {formatChartAxis(max * t)}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const barH = (d.value / max) * innerH;
        const x = padL + i * slot + (slot - barW) / 2;
        const y = padT + innerH - barH;
        const dayNum = i + 1;
        const showLabel =
          dayNum === 1 || dayNum === lastDay || dayNum % 5 === 0;
        return (
          <g key={d.key}>
            <title>{`Day ${d.label}: ${formatStatMoney(d.value)}`}</title>
            <rect
              x={x}
              y={barH > 0 ? y : padT + innerH - 1}
              width={barW}
              height={barH > 0 ? barH : 1}
              rx="1"
              fill={color}
              opacity={d.value > 0 ? 0.9 : 0.2}
            />
            {showLabel ? (
              <text
                x={x + barW / 2}
                y={height - 6}
                textAnchor="middle"
                className="fill-gray-500"
                fontSize="10"
              >
                {d.label}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

function ChartCard({
  title,
  iconWrap,
  Icon,
  color,
  data,
  loading,
  monthLabel,
  onPrev,
  onNext,
  disableNext,
  panelClass = "bg-white border-gray-100",
}) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  return (
    <div className={`rounded-lg shadow-sm border p-4 ${panelClass}`}>
      <div className="flex items-center gap-2 mb-3">
        <div
          className={`w-8 h-8 rounded-md ${iconWrap} text-white flex items-center justify-center flex-shrink-0`}
        >
          {Icon ? <Icon className="text-sm" /> : null}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          <p className="text-xs tracking-wide text-gray-400 uppercase">
            {monthLabel}
            {!loading ? ` · ${formatStatMoney(total)}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={onPrev}
            className="w-6 h-6 rounded-full bg-gray-700 text-white flex items-center justify-center hover:bg-gray-800"
            title="Previous month"
          >
            <FaChevronLeft className="text-[10px]" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={disableNext}
            className="w-6 h-6 rounded-full bg-gray-700 text-white flex items-center justify-center hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Next month"
          >
            <FaChevronRight className="text-[10px]" />
          </button>
        </div>
      </div>
      {loading ? (
        <div className="h-52 flex items-center justify-center text-sm text-gray-400">
          Loading…
        </div>
      ) : (
        <DailyBarChart data={data} color={color} />
      )}
    </div>
  );
}

const DashboardMonthlyCharts = () => {
  const { isAgent, appUser } = useRole();
  const thisMonth = useMemo(() => currentMonthCursor(), []);
  const [cursor, setCursor] = useState(thisMonth);
  const [trades, setTrades] = useState([]);
  const [commissionEfts, setCommissionEfts] = useState([]);
  const [realEstateEfts, setRealEstateEfts] = useState([]);
  const [generalEfts, setGeneralEfts] = useState([]);
  const [loading, setLoading] = useState(true);

  const disableNext =
    cursor.year > thisMonth.year ||
    (cursor.year === thisMonth.year && cursor.month >= thisMonth.month);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [tradesRes, ctRes, retRes, gaRes] = await Promise.all([
          axiosInstance.get("/trades"),
          axiosInstance.get("/commission-trust-eft").catch(() => ({ data: [] })),
          axiosInstance
            .get("/real-estate-trust-eft")
            .catch(() => ({ data: [] })),
          isAgent
            ? Promise.resolve({ data: [] })
            : axiosInstance
                .get("/general-account-eft")
                .catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setTrades(tradesRes.data || []);
        setCommissionEfts(ctRes.data || []);
        setRealEstateEfts(retRes.data || []);
        setGeneralEfts(gaRes.data || []);
      } catch (err) {
        console.error("Error loading dashboard monthly charts:", err);
        if (!cancelled) {
          setTrades([]);
          setCommissionEfts([]);
          setRealEstateEfts([]);
          setGeneralEfts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAgent]);

  const scopedUser = isAgent ? appUser : null;
  const agentTradeIds = useMemo(() => {
    if (!scopedUser) return null;
    const ids = new Set();
    for (const trade of trades) {
      if (!tradeBelongsToAgent(trade, scopedUser)) continue;
      if (trade?._id) ids.add(String(trade._id));
    }
    return ids;
  }, [trades, scopedUser]);

  const commission = useMemo(
    () => buildCommissionReceivedForMonth(trades, cursor, scopedUser),
    [trades, cursor, scopedUser],
  );
  const efts = useMemo(
    () =>
      buildEftsPaidOutForMonth(
        commissionEfts,
        realEstateEfts,
        generalEfts,
        cursor,
        scopedUser,
        agentTradeIds,
      ),
    [
      commissionEfts,
      realEstateEfts,
      generalEfts,
      cursor,
      scopedUser,
      agentTradeIds,
    ],
  );
  const monthLabel = monthTitle(cursor);

  return (
    <div className="px-6 pb-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard
          title="Commission Received"
          iconWrap="bg-orange-500"
          Icon={FaChartBar}
          color="#f97316"
          data={commission}
          loading={loading}
          monthLabel={monthLabel}
          onPrev={() => setCursor((c) => shiftMonth(c, -1))}
          onNext={() => setCursor((c) => shiftMonth(c, 1))}
          disableNext={disableNext}
          panelClass="bg-orange-50/80 border-orange-100"
        />
        <ChartCard
          title="EFTs Paid Out"
          iconWrap="bg-blue-900"
          Icon={FaMoneyCheckAlt}
          color="#1e3a8a"
          data={efts}
          loading={loading}
          monthLabel={monthLabel}
          onPrev={() => setCursor((c) => shiftMonth(c, -1))}
          onNext={() => setCursor((c) => shiftMonth(c, 1))}
          disableNext={disableNext}
          panelClass="bg-blue-50/80 border-blue-100"
        />
      </div>
    </div>
  );
};

export default DashboardMonthlyCharts;
